import { crearTabla, mostrarToast } from '@plataforma/ui';
import {
  esCorreoValido,
  esSlugValido,
  escapeAttr,
  escaparHtml,
  generarSlugSugerido,
} from '@plataforma/utils';

import {
  cambiarEstadoEmpresa,
  cambiarPlanEmpresa,
  desactivarTienda,
  eliminarAuthUsuarios,
  eliminarEmpresa,
  listarEmpresasBackoffice,
  listarPlanesBackoffice,
  obtenerEmpresaDetalle,
  preflightEliminarEmpresa,
} from '../api/backoffice.js';
import { crearEmpresa } from '../api/empresas.js';
import { exigirSuperAdmin } from '../guardas.js';

import {
  crearBadge,
  crearPaginacion,
  diasRestantes,
  estadoCarga,
  estadoError,
  ESTADOS_CONTRATACION,
  ESTADOS_EMPRESA,
  ESTADOS_PAGO,
  ESTADOS_SUSCRIPCION,
  ESTADOS_TRIAL,
  fecha,
  fechaHora,
  moneda,
  motivoRapido,
  obtenerSuscripcionActual,
} from './backoffice-ui.js';
import { montarShell } from './shell.js';

const FILTROS_INICIALES = {
  busqueda: '',
  estado: '',
  planId: '',
  trialEstado: '',
  pagina: 1,
};

export async function vistaEmpresas(contenedorRaiz) {
  if (!exigirSuperAdmin()) return;

  const contenido = montarShell(contenedorRaiz, '/empresas');
  const estadoVista = { ...FILTROS_INICIALES, planes: [] };

  contenido.innerHTML = `
    <div class="pf-backoffice-header">
      <div>
        <h1>Empresas</h1>
        <p class="pf-texto-suave">Gestión comercial, operativa y administrativa de tenants.</p>
      </div>
      <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-nueva-empresa">Nueva empresa</button>
    </div>

    <form class="pf-tarjeta pf-backoffice-toolbar" id="pf-filtros-empresas">
      <div class="pf-campo">
        <label for="pf-buscar-empresa">Buscar</label>
        <input id="pf-buscar-empresa" type="search" placeholder="Nombre, slug o subdominio" />
      </div>
      <div class="pf-campo">
        <label for="pf-estado-empresa">Estado</label>
        <select id="pf-estado-empresa">
          <option value="">Todos</option>
          <option value="borrador">Borrador</option>
          <option value="en_prueba">En prueba</option>
          <option value="activa">Activa</option>
          <option value="suspendida">Suspendida</option>
          <option value="dada_de_baja">Dada de baja</option>
        </select>
      </div>
      <div class="pf-campo">
        <label for="pf-plan-empresa">Plan</label>
        <select id="pf-plan-empresa"><option value="">Todos</option></select>
      </div>
      <div class="pf-campo">
        <label for="pf-trial-empresa">Trial</label>
        <select id="pf-trial-empresa">
          <option value="">Todos</option>
          <option value="activa">Activo</option>
          <option value="expirada">Vencido</option>
          <option value="convertida">Convertido</option>
        </select>
      </div>
      <button type="submit" class="pf-boton pf-boton--secundario">Filtrar</button>
    </form>

    <div id="pf-empresas-tabla">${estadoCarga()}</div>
    <pf-modal titulo="Nueva empresa" id="pf-modal-nueva-empresa"></pf-modal>
    <pf-modal titulo="Empresa creada" id="pf-modal-credenciales"></pf-modal>
    <pf-modal titulo="Detalle de empresa" id="pf-modal-empresa-detalle"></pf-modal>
    <pf-modal titulo="Cambiar plan" id="pf-modal-cambiar-plan"></pf-modal>
    <pf-modal titulo="Eliminar empresa" id="pf-modal-eliminar-empresa"></pf-modal>
  `;

  const { data: planes } = await listarPlanesBackoffice();
  estadoVista.planes = planes ?? [];
  const selectPlan = contenido.querySelector('#pf-plan-empresa');
  selectPlan.insertAdjacentHTML(
    'beforeend',
    estadoVista.planes.map((p) => `<option value="${p.id}">${p.nombre}</option>`).join(''),
  );

  contenido.querySelector('#pf-filtros-empresas').addEventListener('submit', async (evento) => {
    evento.preventDefault();
    estadoVista.busqueda = contenido.querySelector('#pf-buscar-empresa').value;
    estadoVista.estado = contenido.querySelector('#pf-estado-empresa').value;
    estadoVista.planId = contenido.querySelector('#pf-plan-empresa').value;
    estadoVista.trialEstado = contenido.querySelector('#pf-trial-empresa').value;
    estadoVista.pagina = 1;
    await cargarTabla(contenido, estadoVista);
  });

  contenido.querySelector('#pf-btn-nueva-empresa').addEventListener('click', () => {
    montarFormularioAlta(
      contenido.querySelector('#pf-modal-nueva-empresa'),
      contenido.querySelector('#pf-modal-credenciales'),
      estadoVista.planes.filter((p) => p.estado === 'activo'),
      () => cargarTabla(contenido, estadoVista),
    );
  });

  await cargarTabla(contenido, estadoVista);
}

async function cargarTabla(contenido, estadoVista) {
  const destino = contenido.querySelector('#pf-empresas-tabla');
  destino.innerHTML = estadoCarga();

  const { data, error, count } = await listarEmpresasBackoffice(estadoVista);

  if (error) {
    destino.innerHTML = estadoError('No se pudieron cargar las empresas.');
    return;
  }

  const tabla = crearTabla({
    filaVacia: 'No hay empresas con estos filtros.',
    columnas: [
      { clave: 'nombre', titulo: 'Empresa' },
      { titulo: 'Subdominio', render: (fila) => `${fila.subdominio}.vimosdigital.com` },
      { titulo: 'Plan', render: (fila) => fila.plan?.nombre ?? '—' },
      {
        titulo: 'Suscripción',
        render: (fila) => {
          const suscripcion = obtenerSuscripcionActual(fila);
          return suscripcion ? crearBadge(ESTADOS_SUSCRIPCION, suscripcion.estado) : '—';
        },
      },
      { titulo: 'Estado', render: (fila) => crearBadge(ESTADOS_EMPRESA, fila.estado) },
      { titulo: 'Trial', render: (fila) => crearBadge(ESTADOS_TRIAL, fila.trial_estado) },
      { titulo: 'Creación', render: (fila) => fecha(fila.creado_en) },
      {
        titulo: 'Acciones',
        render: (fila) => accionesEmpresa(contenido, fila, estadoVista),
      },
    ],
    filas: data ?? [],
  });

  destino.innerHTML = '';
  destino.appendChild(tabla);
  destino.appendChild(
    crearPaginacion({
      total: count ?? 0,
      pagina: estadoVista.pagina,
      alCambiar: async (pagina) => {
        estadoVista.pagina = pagina;
        await cargarTabla(contenido, estadoVista);
      },
    }),
  );
}

function accionesEmpresa(contenido, empresa, estadoVista) {
  const contenedor = document.createElement('div');
  contenedor.className = 'pf-backoffice-actions';

  const detalle = document.createElement('button');
  detalle.type = 'button';
  detalle.className = 'pf-boton pf-boton--secundario pf-boton--chico';
  detalle.textContent = 'Detalle';
  detalle.addEventListener('click', () => abrirDetalleEmpresa(contenido, empresa.id, estadoVista));
  contenedor.appendChild(detalle);

  const esBorrador = empresa.estado === 'borrador';

  const cambioPlan = document.createElement('button');
  if (!esBorrador) {
    cambioPlan.type = 'button';
    cambioPlan.className = 'pf-boton pf-boton--secundario pf-boton--chico';
    cambioPlan.textContent = 'Cambiar plan';
    cambioPlan.addEventListener('click', () => abrirCambioPlan(contenido, empresa, estadoVista));
    contenedor.appendChild(cambioPlan);
  }

  const acciones = {
    borrador: [{ etiqueta: 'Descartar', estado: 'dada_de_baja' }],
    en_prueba: [{ etiqueta: 'Activar', estado: 'activa' }],
    activa: [{ etiqueta: 'Suspender', estado: 'suspendida' }],
    suspendida: [{ etiqueta: 'Reactivar', estado: 'activa' }],
    dada_de_baja: [],
  };

  for (const accion of acciones[empresa.estado] ?? []) {
    const boton = document.createElement('button');
    boton.type = 'button';
    boton.className = 'pf-boton pf-boton--secundario pf-boton--chico';
    boton.textContent = accion.etiqueta;
    boton.addEventListener('click', async () => {
      const motivo = motivoRapido(`Motivo para ${accion.etiqueta.toLowerCase()} ${empresa.nombre}`);
      if (!motivo) return;
      boton.disabled = true;
      const { error } = await cambiarEstadoEmpresa(empresa.id, accion.estado, motivo);
      boton.disabled = false;
      if (error) {
        mostrarToast(error.message ?? 'No se pudo cambiar el estado.', { tono: 'peligro' });
        return;
      }
      mostrarToast('Estado actualizado y auditado.', { tono: 'exito' });
      await cargarTabla(contenido, estadoVista);
    });
    contenedor.appendChild(boton);
  }

  // Botón Eliminar — solo visible si la empresa no está marcada como protegida
  const btnEliminar = document.createElement('button');
  btnEliminar.type = 'button';
  btnEliminar.className = 'pf-boton pf-boton--peligro pf-boton--chico';
  btnEliminar.textContent = 'Eliminar';
  btnEliminar.addEventListener('click', () =>
    abrirModalEliminarEmpresa(contenido, empresa.id, estadoVista),
  );
  contenedor.appendChild(btnEliminar);

  return contenedor;
}

async function abrirDetalleEmpresa(contenido, empresaId, estadoVista) {
  const modal = contenido.querySelector('#pf-modal-empresa-detalle');
  modal.innerHTML = estadoCarga('Cargando detalle...');
  modal.abrir();

  const { data, error } = await obtenerEmpresaDetalle(empresaId);

  if (error) {
    modal.innerHTML = estadoError('No se pudo cargar el detalle de la empresa.');
    return;
  }

  const tabs = [
    ['resumen', 'Resumen'],
    ['usuarios', 'Usuarios'],
    ['suscripciones', 'Suscripción'],
    ['contrataciones', 'Contrataciones'],
    ['pagos', 'Pagos'],
    ['dominios', 'Dominios'],
    ['publicaciones', 'Publicaciones'],
    ['auditoria', 'Auditoría'],
  ];

  modal.innerHTML = `
    <div class="pf-backoffice-tabs">
      ${tabs.map(([id, etiqueta]) => `<button type="button" class="pf-backoffice-tab" data-tab="${id}">${etiqueta}</button>`).join('')}
    </div>
    <div id="pf-detalle-tab"></div>
  `;

  const destino = modal.querySelector('#pf-detalle-tab');

  function render(tab) {
    modal.querySelectorAll('.pf-backoffice-tab').forEach((boton) => {
      boton.classList.toggle('pf-backoffice-tab--activo', boton.dataset.tab === tab);
    });
    destino.innerHTML = '';
    destino.appendChild(renderTabEmpresa(tab, data, contenido, estadoVista, modal));
  }

  modal.querySelectorAll('.pf-backoffice-tab').forEach((boton) => {
    boton.addEventListener('click', () => render(boton.dataset.tab));
  });

  render('resumen');
  await cargarTabla(contenido, estadoVista);
}

function renderTabEmpresa(tab, data, contenido, estadoVista, modal) {
  const contenedor = document.createElement('div');
  const empresa = data.empresa;

  if (tab === 'resumen') {
    contenedor.innerHTML = `
      <div class="pf-backoffice-detail">
        <div class="pf-backoffice-kv"><span>Nombre</span><strong>${escaparHtml(empresa.nombre)}</strong></div>
        <div class="pf-backoffice-kv"><span>Slug</span><strong>${escaparHtml(empresa.slug)}</strong></div>
        <div class="pf-backoffice-kv"><span>Subdominio</span><strong>${escaparHtml(empresa.subdominio)}.vimosdigital.com</strong></div>
        <div class="pf-backoffice-kv"><span>Estado</span><strong>${ESTADOS_EMPRESA[empresa.estado]?.etiqueta ?? escaparHtml(empresa.estado)}</strong></div>
        <div class="pf-backoffice-kv"><span>Plan actual</span><strong>${escaparHtml(empresa.plan?.nombre ?? '—')} · ${moneda(empresa.plan?.precio_mensual)}</strong></div>
        <div class="pf-backoffice-kv"><span>Trial</span><strong>${ESTADOS_TRIAL[empresa.trial_estado]?.etiqueta ?? escaparHtml(empresa.trial_estado)} · ${diasRestantes(empresa.trial_fin_en)}</strong></div>
        <div class="pf-backoffice-kv"><span>Creación</span><strong>${fechaHora(empresa.creado_en)}</strong></div>
        <div class="pf-backoffice-kv"><span>Actualización</span><strong>${fechaHora(empresa.actualizado_en)}</strong></div>
      </div>
      <div class="pf-formulario__footer" style="margin-top:16px;">
        <button type="button" class="pf-boton pf-boton--peligro" id="pf-btn-eliminar-desde-detalle">
          Eliminar empresa
        </button>
      </div>
    `;

    contenedor.querySelector('#pf-btn-eliminar-desde-detalle').addEventListener('click', () => {
      modal.cerrar();
      abrirModalEliminarEmpresa(contenido, empresa.id, estadoVista);
    });

    return contenedor;
  }

  const tablas = {
    usuarios: {
      filas: data.usuarios,
      vacio: 'No hay usuarios asociados.',
      columnas: [
        { clave: 'nombre_completo', titulo: 'Nombre' },
        { clave: 'rol', titulo: 'Rol' },
        { clave: 'estado', titulo: 'Estado' },
        { titulo: 'Creación', render: (fila) => fecha(fila.creado_en) },
      ],
    },
    suscripciones: {
      filas: data.suscripciones,
      vacio: 'No hay suscripciones.',
      columnas: [
        { titulo: 'Plan', render: (fila) => fila.plan?.nombre ?? '—' },
        { titulo: 'Estado', render: (fila) => crearBadge(ESTADOS_SUSCRIPCION, fila.estado) },
        { titulo: 'Inicio', render: (fila) => fecha(fila.inicia_en) },
        { titulo: 'Fin', render: (fila) => fecha(fila.fin_en) },
        { titulo: 'Precio', render: (fila) => moneda(fila.plan?.precio_mensual) },
      ],
    },
    contrataciones: {
      filas: data.contrataciones,
      vacio: 'No hay contrataciones.',
      columnas: [
        { titulo: 'Plan', render: (fila) => fila.plan?.nombre ?? '—' },
        { titulo: 'Estado', render: (fila) => crearBadge(ESTADOS_CONTRATACION, fila.estado) },
        { titulo: 'Monto', render: (fila) => moneda(fila.monto) },
        { titulo: 'Fecha', render: (fila) => fechaHora(fila.creado_en) },
      ],
    },
    pagos: {
      filas: data.pagos,
      vacio: 'No hay pagos registrados.',
      columnas: [
        { titulo: 'Periodo', render: (fila) => fecha(fila.periodo) },
        { titulo: 'Monto', render: (fila) => moneda(fila.monto) },
        { titulo: 'Estado', render: (fila) => crearBadge(ESTADOS_PAGO, fila.estado) },
        { titulo: 'Pago', render: (fila) => fechaHora(fila.fecha_pago) },
      ],
    },
    dominios: {
      filas: data.dominios,
      vacio: 'No hay dominios.',
      columnas: [
        { clave: 'valor', titulo: 'Dominio' },
        { clave: 'tipo', titulo: 'Tipo' },
        { clave: 'estado_dns', titulo: 'DNS' },
        { titulo: 'Principal', render: (fila) => (fila.es_principal ? 'Sí' : 'No') },
      ],
    },
    publicaciones: {
      filas: data.publicaciones,
      vacio: 'No hay publicaciones.',
      columnas: [
        { clave: 'estado', titulo: 'Estado' },
        { titulo: 'Inicio', render: (fila) => fechaHora(fila.iniciado_en) },
        { titulo: 'Fin', render: (fila) => fechaHora(fila.finalizado_en) },
        { titulo: 'Archivos', render: (fila) => fila.archivos_generados ?? '—' },
      ],
    },
    auditoria: {
      filas: data.auditoria,
      vacio: 'No hay auditoría.',
      columnas: [
        { clave: 'accion', titulo: 'Acción' },
        { clave: 'entidad', titulo: 'Entidad' },
        { titulo: 'Fecha', render: (fila) => fechaHora(fila.creado_en) },
      ],
    },
  };

  contenedor.appendChild(crearTabla(tablas[tab]));
  return contenedor;
}

function abrirCambioPlan(contenido, empresa, estadoVista) {
  const modal = contenido.querySelector('#pf-modal-cambiar-plan');
  const planesActivos = estadoVista.planes.filter((plan) => plan.estado === 'activo');

  modal.innerHTML = `
    <form id="pf-form-cambio-plan" novalidate>
      <div class="pf-detalle-backoffice">
        <div class="pf-detalle-backoffice-kv"><span>Empresa</span><strong>${escaparHtml(empresa.nombre)}</strong></div>
        <div class="pf-detalle-backoffice-kv"><span>Plan actual</span><strong>${escaparHtml(empresa.plan?.nombre ?? '—')} · ${moneda(empresa.plan?.precio_mensual)}</strong></div>
      </div>
      <div class="pf-campo" style="margin-top: 16px;">
        <label for="pf-plan-ids">Nuevo plan</label>
        <select id="pf-plan-ids" required>
          ${planesActivos.map((p) => `<option value="${escaparHtml(p.id)}">${escaparHtml(p.nombre)} · ${moneda(p.precio_mensual)}</option>`).join('')}
        </select>
      </div>
      <div class="pf-campo">
        <label for="pf-plan-activo">Motivo</label>
        <textarea id="pf-plan-activo" rows="3" required></textarea>
        <small>Un cambio a un plan inferior no elimina datos; solo restringe funciones por límites del plan.</small>
      </div>
      <p class="pf-campo__error" id="pf-error-cambio-plan" hidden></p>
      <div class="pf-formulario__footer">
        <button type="button" class="pf-boton pf-boton--secundario" id="pf-cancelar-cambio-plan">Cancelar</button>
        <button type="submit" class="pf-boton pf-boton--primario">Confirmar cambio de plan</button>
      </div>
    </form>
  `;

  modal.querySelector('#pf-cancelar-cambio-plan').addEventListener('click', () => modal.cerrar());
  modal.querySelector('#pf-form-cambio-plan').addEventListener('submit', async (evento) => {
    evento.preventDefault();
    const nuevoPlanId = modal.querySelector('#pf-plan-ids').value;
    const motivo = modal.querySelector('#pf-plan-activo').value.trim();
    const errorEl = modal.querySelector('#pf-error-cambio-plan');
    errorEl.hidden = true;
    if (!motivo) {
      errorEl.textContent = 'El motivo es obligatorio.';
      errorEl.hidden = false;
      return;
    }
    const { data, error } = await cambiarPlanEmpresa(empresa.id, nuevoPlanId, motivo);
    if (error) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
      return;
    }
    mostrarToast(`Plan actualizado: ${data?.tipo_cambio ?? 'cambio'}.`, { tono: 'exito' });
    modal.cerrar();
    await cargarTabla(contenido, estadoVista);
  });

  modal.abrir();
}

// ============================================================================
// Modal de eliminación de empresa — confirmación fuerte
// ============================================================================

/**
 * Valida si las condiciones del modal de confirmación están completas.
 * Exportada para facilitar tests unitarios sin montar DOM complejo.
 *
 * @param {{ nombreIngresado: string, nombreEsperado: string, checkConfirmado: boolean, motivo: string }} params
 * @returns {boolean}
 */
export function validarConfirmacionEliminar({
  nombreIngresado,
  nombreEsperado,
  checkConfirmado,
  motivo,
}) {
  return (
    nombreIngresado.trim() === nombreEsperado.trim() &&
    checkConfirmado === true &&
    motivo.trim().length > 0
  );
}

export async function abrirModalEliminarEmpresa(contenido, empresaId, estadoVista) {
  const modal = contenido.querySelector('#pf-modal-eliminar-empresa');
  modal.innerHTML = estadoCarga('Verificando empresa...');
  modal.abrir();

  const { data: preflight, error: errorPreflight } = await preflightEliminarEmpresa(empresaId);

  if (errorPreflight) {
    modal.innerHTML = estadoError(errorPreflight.message ?? 'No se pudo verificar la empresa.');
    return;
  }

  const e = preflight.empresa;
  const r = preflight.resumen;

  if (e.es_protegida) {
    modal.innerHTML = `
      <div class="pf-backoffice-detail">
        <p class="pf-campo__error" style="margin-bottom:12px;">
          ⛔ Esta empresa está marcada como protegida y no puede eliminarse.
        </p>
        <div class="pf-backoffice-kv"><span>Empresa</span><strong>${escaparHtml(e.nombre)}</strong></div>
      </div>
      <div class="pf-formulario__footer">
        <button type="button" class="pf-boton pf-boton--primario" id="pf-cerrar-protegida">Cerrar</button>
      </div>
    `;
    modal.querySelector('#pf-cerrar-protegida').addEventListener('click', () => modal.cerrar());
    return;
  }

  modal.innerHTML = `
    <div class="pf-backoffice-detail">
      <div class="pf-backoffice-kv"><span>Empresa</span><strong>${escaparHtml(e.nombre)}</strong></div>
      <div class="pf-backoffice-kv"><span>Slug</span><strong>${escaparHtml(e.slug)}</strong></div>
      <div class="pf-backoffice-kv"><span>Subdominio</span><strong>${escaparHtml(e.subdominio)}.vimosdigital.com</strong></div>
      <div class="pf-backoffice-kv"><span>Estado</span><strong>${ESTADOS_EMPRESA[e.estado]?.etiqueta ?? escaparHtml(e.estado)}</strong></div>
      <div class="pf-backoffice-kv"><span>Plan</span><strong>${escaparHtml(e.plan_nombre)} · ${moneda(e.plan_precio)}/mes</strong></div>
      <div class="pf-backoffice-kv"><span>Suscripción</span><strong>${escaparHtml(r.suscripcion_estado)}</strong></div>
      <div class="pf-backoffice-kv pf-backoffice-kv--alerta"><span>Usuarios</span><strong>${r.n_usuarios}</strong></div>
      <div class="pf-backoffice-kv pf-backoffice-kv--alerta"><span>Productos</span><strong>${r.n_productos}</strong></div>
      <div class="pf-backoffice-kv pf-backoffice-kv--alerta"><span>Categorías</span><strong>${r.n_categorias}</strong></div>
      <div class="pf-backoffice-kv pf-backoffice-kv--alerta"><span>Cupones</span><strong>${r.n_cupones}</strong></div>
    </div>

<p style="color:var(--pf-color-peligro,#b91c1c);font-weight:600;margin-bottom:12px;">
      Aviso: Esta acción eliminará permanentemente la empresa y todos sus datos asociados.
      Esta operación no se puede deshacer.
    </p>

    <form id="pf-form-eliminar-empresa" novalidate>
      <div class="pf-campo">
        <label for="pf-confirmar-nombre">Escribe el nombre exacto de la empresa para confirmar:</label>
        <input
          id="pf-confirmar-nombre"
          type="text"
          placeholder="${escapeAttr(e.nombre)}"
          autocomplete="off"
          aria-describedby="pf-hint-nombre"
        />
        <small id="pf-hint-nombre">Debe coincidir exactamente: <strong>${escaparHtml(e.nombre)}</strong></small>
      </div>

      <div class="pf-campo" style="display:flex;align-items:flex-start;gap:8px;margin-bottom:12px;">
        <input type="checkbox" id="pf-check-permanente" style="margin-top:3px;flex-shrink:0;" />
        <label for="pf-check-permanente" style="cursor:pointer;">
          Entiendo que esta acción es permanente e irreversible.
        </label>
      </div>

      <div class="pf-campo">
        <label for="pf-motivo-eliminar">Motivo de la eliminación</label>
        <textarea id="pf-motivo-eliminar" rows="3" placeholder="Ej. Limpieza de empresa de prueba, cliente canceló, etc."></textarea>
      </div>

      <p class="pf-campo__error" id="pf-error-eliminar" hidden></p>

      <div class="pf-formulario__footer">
        <button type="button" class="pf-boton pf-boton--secundario" id="pf-cancelar-eliminar">Cancelar</button>
        <button
          type="submit"
          class="pf-boton pf-boton--peligro"
          id="pf-submit-eliminar"
          disabled
        >
          Eliminar definitivamente
        </button>
      </div>
    </form>
  `;

  const inputNombre = modal.querySelector('#pf-confirmar-nombre');
  const checkPermanente = modal.querySelector('#pf-check-permanente');
  const inputMotivo = modal.querySelector('#pf-motivo-eliminar');
  const btnSubmit = modal.querySelector('#pf-submit-eliminar');
  const errorEl = modal.querySelector('#pf-error-eliminar');

  function actualizarBoton() {
    btnSubmit.disabled = !validarConfirmacionEliminar({
      nombreIngresado: inputNombre.value,
      nombreEsperado: e.nombre,
      checkConfirmado: checkPermanente.checked,
      motivo: inputMotivo.value,
    });
  }

  inputNombre.addEventListener('input', actualizarBoton);
  checkPermanente.addEventListener('change', actualizarBoton);
  inputMotivo.addEventListener('input', actualizarBoton);

  modal.querySelector('#pf-cancelar-eliminar').addEventListener('click', () => modal.cerrar());

  modal.querySelector('#pf-form-eliminar-empresa').addEventListener('submit', async (evento) => {
    evento.preventDefault();
    errorEl.hidden = true;

    const motivo = inputMotivo.value.trim();

    if (
      !validarConfirmacionEliminar({
        nombreIngresado: inputNombre.value,
        nombreEsperado: e.nombre,
        checkConfirmado: checkPermanente.checked,
        motivo,
      })
    ) {
      errorEl.textContent = 'Completa todos los campos de confirmación correctamente.';
      errorEl.hidden = false;
      return;
    }

    btnSubmit.disabled = true;
    btnSubmit.textContent = 'Eliminando...';

    const { data: resultado, error: errorEliminar } = await eliminarEmpresa(empresaId, motivo);

    if (errorEliminar) {
      // Idempotencia: si la empresa ya no existe (borrada por un intento
      // previo), igual reintentamos la limpieza de auth.users pendientes.
      if (errorEliminar.message?.includes('no encontrada')) {
        await eliminarAuthUsuarios({ empresaId });
        modal.cerrar();
        mostrarToast('La empresa ya había sido eliminada. Verificando limpieza de accesos.', {
          tono: 'exito',
        });
        await cargarTabla(contenido, estadoVista);
        return;
      }
      errorEl.textContent = errorEliminar.message ?? 'No se pudo eliminar la empresa.';
      errorEl.hidden = false;
      btnSubmit.disabled = false;
      btnSubmit.textContent = 'Eliminar definitivamente';
      return;
    }

    // Segunda fase: borrar auth.users en el servidor (Edge Function, service_role).
    // Es idempotente: la Edge Function lee los pendientes por empresa_id y
    // reintenta sin dejar estado inconsistente.
    const { data: authRes } = await eliminarAuthUsuarios({
      empresaId: resultado?.empresa_id ?? empresaId,
    });

    const authFallidos = authRes?.fallidos ?? [];

    // Tercera fase: desactivar tienda pública (Edge Function, service_role).
    // Escribe estado.json (disponible=false, 410) para que el Edge Gateway
    // rechace tráfico de forma inmediata, y elimina archivos publicados de
    // Storage. Idempotente — seguro de reintentar.
    const slug = resultado?.slug;
    let tiendaDesactivada = false;
    if (slug) {
      const { error: tiendaError } = await desactivarTienda({ slug });
      tiendaDesactivada = !tiendaError;
      if (tiendaError) {
        console.error('[eliminar-empresa] Error desactivando tienda:', tiendaError.message);
      }
    }

    const totalProblemas = authFallidos.length + (tiendaDesactivada === false && slug ? 1 : 0);
    if (totalProblemas > 0) {
      const partes = [];
      if (authFallidos.length > 0) {
        partes.push(`${authFallidos.length || 'algunos'} accesos no se pudieron limpiar`);
      }
      if (!tiendaDesactivada && slug) {
        partes.push('la tienda pública no pudo desactivarse — reintenta la eliminación');
      }
      mostrarToast(
        `Empresa "${resultado?.empresa_eliminada ?? e.nombre}" eliminada. ` +
          `Pero ${partes.join(' y ')}; reintenta la eliminación para completar la limpieza.`,
        { tono: 'peligro' },
      );
    } else {
      mostrarToast(
        `Empresa "${resultado?.empresa_eliminada ?? e.nombre}" eliminada. ` +
          `Usuarios: ${resultado?.usuarios_eliminados ?? 0}, ` +
          `Productos: ${resultado?.productos_eliminados ?? 0}, ` +
          `Accesos: ${authRes?.eliminados?.length ?? 0}, ` +
          `Tienda: desactivada.`,
        { tono: 'exito' },
      );
    }

    modal.cerrar();
    await cargarTabla(contenido, estadoVista);
  });
}

// ============================================================================
// Alta de empresa
// ============================================================================

function montarFormularioAlta(modal, modalCredenciales, planes, alCrear) {
  modal.innerHTML = `
    <form id="pf-form-alta-empresa" novalidate>
      <div class="pf-campo">
        <label for="pf-nombre-empresa">Nombre comercial</label>
        <input type="text" id="pf-nombre-empresa" required />
      </div>
      <div class="pf-campo">
        <label for="pf-slug">Slug</label>
        <input type="text" id="pf-slug" required />
      </div>
      <div class="pf-campo">
        <label for="pf-subdominio">Subdominio</label>
        <input type="text" id="pf-subdominio" required />
        <small id="pf-subdominio-preview">tuempresa.vimosdigital.com</small>
      </div>
      <div class="pf-campo">
        <label for="pf-plan">Plan</label>
        <select id="pf-plan" required>
          ${planes.map((p) => `<option value="${p.id}">${p.nombre} · ${moneda(p.precio_mensual)}/mes</option>`).join('')}
        </select>
      </div>
      <div class="pf-campo">
        <label for="pf-nombre-titular">Nombre completo del titular</label>
        <input type="text" id="pf-nombre-titular" required />
      </div>
      <div class="pf-campo">
        <label for="pf-email-titular">Correo del titular</label>
        <input type="email" id="pf-email-titular" required />
      </div>
      <p class="pf-campo__error" id="pf-alta-error" hidden></p>
      <div class="pf-formulario__footer">
        <button type="submit" class="pf-boton pf-boton--primario" id="pf-alta-submit">Crear empresa</button>
      </div>
    </form>
  `;

  const campoNombre = modal.querySelector('#pf-nombre-empresa');
  const campoSlug = modal.querySelector('#pf-slug');
  const campoSubdominio = modal.querySelector('#pf-subdominio');
  const previewSubdominio = modal.querySelector('#pf-subdominio-preview');
  let slugManual = false;
  let subdominioManual = false;

  campoSlug.addEventListener('input', () => {
    slugManual = true;
  });
  campoSubdominio.addEventListener('input', () => {
    subdominioManual = true;
    previewSubdominio.textContent = `${campoSubdominio.value || 'tuempresa'}.vimosdigital.com`;
  });
  campoNombre.addEventListener('input', () => {
    const sugerido = generarSlugSugerido(campoNombre.value);
    if (!slugManual) campoSlug.value = sugerido;
    if (!subdominioManual) campoSubdominio.value = sugerido;
    previewSubdominio.textContent = `${campoSubdominio.value || 'tuempresa'}.vimosdigital.com`;
  });

  modal.querySelector('#pf-form-alta-empresa').addEventListener('submit', async (evento) => {
    evento.preventDefault();
    const errorEl = modal.querySelector('#pf-alta-error');
    const submit = modal.querySelector('#pf-alta-submit');
    errorEl.hidden = true;

    const datos = {
      nombre_empresa: campoNombre.value.trim(),
      slug: campoSlug.value.trim().toLowerCase(),
      subdominio: campoSubdominio.value.trim().toLowerCase(),
      plan_id: modal.querySelector('#pf-plan').value,
      nombre_completo_titular: modal.querySelector('#pf-nombre-titular').value.trim(),
      email_titular: modal.querySelector('#pf-email-titular').value.trim().toLowerCase(),
    };

    const errores = [];
    if (datos.nombre_empresa.length < 2) errores.push('El nombre de la empresa es obligatorio.');
    if (!esSlugValido(datos.slug)) errores.push('El slug no tiene formato válido.');
    if (!esSlugValido(datos.subdominio)) errores.push('El subdominio no tiene formato válido.');
    if (!datos.plan_id) errores.push('Selecciona un plan.');
    if (datos.nombre_completo_titular.length < 2) errores.push('El titular es obligatorio.');
    if (!esCorreoValido(datos.email_titular)) errores.push('El correo del titular no es válido.');

    if (errores.length) {
      errorEl.textContent = errores.join(' ');
      errorEl.hidden = false;
      return;
    }

    submit.disabled = true;
    submit.textContent = 'Creando...';
    const { data, error } = await crearEmpresa(datos);
    submit.disabled = false;
    submit.textContent = 'Crear empresa';

    if (error) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
      return;
    }

    modal.cerrar();
    mostrarCredenciales(modalCredenciales, data);
    await alCrear();
  });

  modal.abrir();
}

function mostrarCredenciales(modal, data) {
  const urlPanelCliente = import.meta.env?.VITE_PANEL_CLIENTE_URL ?? '';
  modal.innerHTML = `
    <p class="pf-texto-suave" style="margin-bottom: 16px;">Copia estos datos ahora. La contraseña temporal no se vuelve a mostrar.</p>
    <div class="pf-campo"><label>URL del panel</label><input readonly value="${urlPanelCliente || 'Configura VITE_PANEL_CLIENTE_URL'}" /></div>
    <div class="pf-campo"><label>Correo</label><input readonly value="${data.email_titular}" /></div>
    <div class="pf-campo"><label>Contraseña temporal</label><input readonly value="${data.password_temporal}" style="font-family: monospace;" /></div>
    <div class="pf-formulario__footer"><button type="button" class="pf-boton pf-boton--primario" id="pf-cred-listo">Listo</button></div>
  `;
  modal.querySelector('#pf-cred-listo').addEventListener('click', () => modal.cerrar());
  modal.abrir();
}
